import { Country, auStates, ukStates, usStates } from "./country";

type Verb = {
  past: string;
  present: string;
  future: string;
};

type Term = {
  singular: string;
  plural: string;
  title: string;
  header: string;
  name: string;
};

export type Locale = Readonly<{
  country: Country;
  locale: "en-AU" | "en-US" | "en-GB";
  currency: "AUD" | "USD" | "GBP";
  currencySymbol: "$" | "£";
  dateFormat: ("day" | "month" | "year")[];
  states: { name: string; displayName: string }[];
  terms: {
    digitise: Verb;
    practise: Verb;
    customise: Verb;
    finalise: Verb;
    state: Term;
    familyPro: Term;
    enquiry: Term;
    responsiblePerson: Term;
    colour: Term;
    organisation: Term;
    acknowledgement: Term;
    matterNumber: Term;
  };
  dateDisplayFormat: string;
}>;

export const auLocale: Locale = {
  country: "AU",
  locale: "en-AU",
  currency: "AUD",
  currencySymbol: "$",
  states: auStates.map((state) => ({
    name: state.name,
    displayName: state.name,
  })),
  dateFormat: ["day", "month", "year"],
  terms: {
    digitise: {
      past: "digitised",
      present: "digitising",
      future: "digitise",
    },
    practise: {
      past: "practised",
      present: "practising",
      future: "practise",
    },
    customise: {
      past: "customised",
      present: "customising",
      future: "customise",
    },
    finalise: {
      past: "finalised",
      present: "finalising",
      future: "finalise",
    },
    state: {
      singular: "a state",
      plural: "states",
      title: "States",
      header: "State",
      name: "state",
    },
    familyPro: {
      singular: "FamilyProperty",
      plural: "FamilyProperty",
      title: "FamilyProperty",
      header: "FamilyProperty",
      name: "FamilyProperty",
    },
    enquiry: {
      singular: "an enquiry",
      plural: "enquiries",
      title: "Enquiry",
      header: "Enquiry",
      name: "enquiry",
    },
    responsiblePerson: {
      singular: "Person Responsible",
      plural: "People Responsible",
      title: "Person Responsible",
      header: "Person Responsible",
      name: "Person Responsible",
    },
    colour: {
      singular: "a colour",
      plural: "colours",
      title: "Colours",
      header: "Colour",
      name: "colour",
    },
    organisation: {
      singular: "an organisation",
      plural: "organisations",
      title: "Organisation",
      header: "Organisation",
      name: "organisation",
    },
    acknowledgement: {
      singular: "an acknowledgement",
      plural: "acknowledgements",
      title: "Acknowledgements",
      header: "Acknowledgement",
      name: "acknowledgement",
    },
    matterNumber: {
      singular: "a matter number",
      plural: "matter numbers",
      title: "Matter Number",
      header: "Matter Number",
      name: "matter number",
    },
  },
  dateDisplayFormat: "dd/MM/yyyy hh:mm a",
};

export const usLocale: Locale = {
  country: "US",
  locale: "en-US",
  currency: "USD",
  currencySymbol: "$",
  states: usStates.map((state) => ({
    name: state.name,
    displayName: state.name,
  })),
  dateFormat: ["month", "day", "year"],
  terms: {
    digitise: {
      past: "digitized",
      present: "digitizing",
      future: "digitize",
    },
    practise: {
      past: "practiced",
      present: "practicing",
      future: "practice",
    },
    customise: {
      past: "customized",
      present: "customizing",
      future: "customize",
    },
    finalise: {
      past: "finalized",
      present: "finalizing",
      future: "finalize",
    },
    state: {
      singular: "a state",
      plural: "states",
      title: "States",
      header: "State",
      name: "state",
    },
    familyPro: {
      singular: "FamilyPro",
      plural: "FamilyPro",
      title: "FamilyPro",
      header: "FamilyPro",
      name: "FamilyPro",
    },
    enquiry: {
      singular: "an inquiry",
      plural: "inquiries",
      title: "Inquiry",
      header: "Inquiry",
      name: "inquiry",
    },
    responsiblePerson: {
      singular: "Attorney Responsible",
      plural: "Attorneys Responsible",
      title: "Attorney Responsible",
      header: "Attorney Responsible",
      name: "Attorney Responsible",
    },
    colour: {
      singular: "a color",
      plural: "colors",
      title: "Colors",
      header: "Color",
      name: "color",
    },
    organisation: {
      singular: "an organization",
      plural: "organizations",
      title: "Organization",
      header: "Organization",
      name: "organization",
    },
    acknowledgement: {
      singular: "an acknowledgment",
      plural: "acknowledgments",
      title: "Acknowledgments",
      header: "Acknowledgment",
      name: "acknowledgment",
    },
    matterNumber: {
      singular: "an Internal Reference",
      plural: "Internal References",
      title: "Internal Reference",
      header: "Internal Reference",
      name: "Internal Reference",
    },
  },
  dateDisplayFormat: "MM/dd/yyyy hh:mm a",
};

export const ukLocale: Locale = {
  country: "GB",
  locale: "en-GB",
  currency: "GBP",
  currencySymbol: "£",
  states: ukStates.map((state) => ({
    name: state.name,
    displayName: state.label,
  })),
  dateFormat: ["day", "month", "year"],
  terms: {
    digitise: {
      past: "digitised",
      present: "digitising",
      future: "digitise",
    },
    practise: {
      past: "practised",
      present: "practising",
      future: "practise",
    },
    customise: {
      past: "customised",
      present: "customising",
      future: "customise",
    },
    finalise: {
      past: "finalised",
      present: "finalising",
      future: "finalise",
    },
    state: {
      singular: "a country",
      plural: "countries",
      title: "Countries",
      header: "Country",
      name: "country",
    },
    familyPro: {
      singular: "FamilyProperty",
      plural: "FamilyProperty",
      title: "FamilyProperty",
      header: "FamilyProperty",
      name: "FamilyProperty",
    },
    enquiry: {
      singular: "an enquiry",
      plural: "enquiries",
      title: "Enquiry",
      header: "Enquiry",
      name: "enquiry",
    },
    responsiblePerson: {
      singular: "Fee Earner",
      plural: "Fee Earners",
      title: "Fee Earner",
      header: "Fee Earner",
      name: "Fee Earner",
    },
    colour: {
      singular: "a colour",
      plural: "colours",
      title: "Colours",
      header: "Colour",
      name: "colour",
    },
    organisation: {
      singular: "an organisation",
      plural: "organisations",
      title: "Organisation",
      header: "Organisation",
      name: "organisation",
    },
    acknowledgement: {
      singular: "an acknowledgement",
      plural: "acknowledgements",
      title: "Acknowledgements",
      header: "Acknowledgement",
      name: "acknowledgement",
    },
    matterNumber: {
      singular: "a matter number",
      plural: "matter numbers",
      title: "Matter Number",
      header: "Matter Number",
      name: "matter number",
    },
  },
  dateDisplayFormat: "dd/MM/yyyy hh:mm a",
};
